// ZeiterfassungDashboardContextProvider.tsx
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useRef,
} from 'react';
import {ApiError, Arbeitszeiteintrag, ArbeitszeitService} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';
import ZeiterfassungDashboardContext, {
  FilterAction,
  IArbeitszeitenData,
  IFilter,
} from './ZeiterfassungDashboardContext';

interface IZeiterfassungContextProviderProps {
  children?: ReactNode;
}

// Filter reducer for consistent filter state updates
const filterReducer = (state: IFilter, action: FilterAction): IFilter => {
  switch (action.type) {
    case 'SET_DEPARTMENT':
      return {
        ...state,
        department: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_BAUVORHABEN':
      return {
        ...state,
        bauvorhaben: action.payload,
      };
    case 'RESET_FILTERS':
      return {
        department: '',
        user: '',
        bauvorhaben: '',
      };
    default:
      return state;
  }
};

// Helper functions
const getBeginningOfCurrentMonth = () => {
  let date = new Date();
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfCurrentMonth = () => {
  let date = new Date();
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  date.setHours(23, 59, 59, 999);
  return date;
};

const parseDateToString = (date: Date) => {
  return (
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    date.getDate().toString().padStart(2, '0')
  );
};

const ZeiterfassungDashboardContextProvider = ({children}: IZeiterfassungContextProviderProps) => {
  const {call_backend_api} = useContext(APIWrapperContext);

  // Use refs for stable function references
  const apiCallRef = useRef(call_backend_api);

  // Update ref when call_backend_api changes
  useEffect(() => {
    apiCallRef.current = call_backend_api;
  }, [call_backend_api]);

  // Date state
  const [selectedDateStart, setSelectedDateStart] = useState<Date>(getBeginningOfCurrentMonth());
  const [selectedDateEnde, setSelectedDateEnde] = useState<Date>(getEndOfCurrentMonth());

  // Data state
  const [arbeitszeiten_alle_MA, setArbeitszeiten_alle_MA] = useState<IArbeitszeitenData>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Filter state using reducer
  const [filter, dispatchFilter] = useReducer(filterReducer, {
    department: '',
    user: '',
    bauvorhaben: '',
  });

  // Reset all filters - create stable function reference
  const resetFilters = useCallback(() => {
    dispatchFilter({type: 'RESET_FILTERS'});
  }, []);

  // Update data from backend - create stable function reference
  const update_Arbeitszeiten_alle_MA = useCallback(() => {
    setIsLoading(true);

    apiCallRef.current(
      ArbeitszeitService.getWorktimesOfAllZeiterfassungsUsersArbeitzeitDashboardWorktimesAllUsersGet(
        parseDateToString(selectedDateStart),
        parseDateToString(selectedDateEnde)
      ),
      (res: any) => {
        setArbeitszeiten_alle_MA(res);
        setIsLoading(false);
      },
      (err: ApiError) => {
        console.error('Error loading data:', err);
        setIsLoading(false);
      },
      undefined,
      false,
      undefined,
      'Daten konnten nicht aktualisiert werden'
    );
  }, [selectedDateStart, selectedDateEnde]);

  const updateArbeitszeiteintrag = useCallback((arbeitszeiteintrag: Arbeitszeiteintrag) => {
    apiCallRef.current(ArbeitszeitService.updateWorktimeArbeitzeitArbeitszeiteintragPut(arbeitszeiteintrag),
      () => {},
      () => {},
      undefined,
      true
    );
  }, []);
  // IMPORTANT CHANGE: Keep all available filter options,
  // even when filters are applied

  // All available users (for user dropdown)
  const availableUsers = useMemo(() => {
    if (!arbeitszeiten_alle_MA.user_zeiterfassung) {
      return [];
    }

    // If department filter is active, show only users from that department
    if (filter.department) {
      return arbeitszeiten_alle_MA.user_zeiterfassung.filter(
        (user) => user.department === filter.department
      );
    }

    // Otherwise show all users
    return [...arbeitszeiten_alle_MA.user_zeiterfassung];
  }, [arbeitszeiten_alle_MA.user_zeiterfassung, filter.department]);

  // All available bauvorhaben (for bauvorhaben dropdown)
  const availableBauvorhaben = useMemo(() => {
    if (!arbeitszeiten_alle_MA.arbeitszeiten) {
      return [];
    }

    // Create a set of all unique BVH values
    const uniqueBauvorhabenSet = new Set<string>();

    // Filter arbeitszeiten by user if user filter is active
    const filteredArbeitszeiten = filter.user
      ? arbeitszeiten_alle_MA.arbeitszeiten.filter((item) => item.user_id === filter.user)
      : filter.department
      ? arbeitszeiten_alle_MA.arbeitszeiten.filter((item) => {
          const user = arbeitszeiten_alle_MA.user_zeiterfassung?.find(
            (u) => u.user_id === item.user_id
          );
          return user && user.department === filter.department;
        })
      : arbeitszeiten_alle_MA.arbeitszeiten;

    // Collect all BVH values
    filteredArbeitszeiten.forEach((item) => {
      if (item.BVH != null && item.BVH !== undefined) {
        uniqueBauvorhabenSet.add(String(item.BVH));
      }
    });

    return Array.from(uniqueBauvorhabenSet).sort();
  }, [
    arbeitszeiten_alle_MA.arbeitszeiten,
    arbeitszeiten_alle_MA.user_zeiterfassung,
    filter.user,
    filter.department,
  ]);

  // For actual display data, apply all filters
  const filteredData = useMemo(() => {
    if (!arbeitszeiten_alle_MA.arbeitszeiten) {
      return {
        arbeitszeiten: [],
        user_zeiterfassung: [],
        notes: [],
        departments: arbeitszeiten_alle_MA.departments || [],
      };
    }

    // Start with all data
    let result = {
      arbeitszeiten: [...arbeitszeiten_alle_MA.arbeitszeiten],
      user_zeiterfassung: [...(arbeitszeiten_alle_MA.user_zeiterfassung || [])],
      notes: [...(arbeitszeiten_alle_MA.notes || [])],
      departments: arbeitszeiten_alle_MA.departments || [],
    };

    // Apply department filter if set
    if (filter.department) {
      // Filter users by department
      result.user_zeiterfassung = result.user_zeiterfassung.filter(
        (user) => user.department === filter.department
      );

      // Get the filtered user IDs
      const userIds = result.user_zeiterfassung.map((user) => user.user_id);

      // Filter arbeitszeiten to only include entries from filtered users
      result.arbeitszeiten = result.arbeitszeiten.filter((item) => userIds.includes(item.user_id));

      // Filter notes to only include entries from filtered users
      result.notes = result.notes.filter((note) => userIds.includes(note.user_id));
    }

    // Apply user filter if set
    if (filter.user) {
      // Filter users to only include the selected user
      result.user_zeiterfassung = result.user_zeiterfassung.filter(
        (user) => user.user_id === filter.user
      );

      // Filter arbeitszeiten to only include entries from the selected user
      result.arbeitszeiten = result.arbeitszeiten.filter((item) => item.user_id === filter.user);

      // Filter notes to only include entries from the selected user
      result.notes = result.notes.filter((note) => note.user_id === filter.user);
    }

    // Apply bauvorhaben filter if set
    if (filter.bauvorhaben) {
      result.arbeitszeiten = result.arbeitszeiten.filter(
        (item) => String(item.BVH) === filter.bauvorhaben
      );
    }

    return result;
  }, [arbeitszeiten_alle_MA, filter.department, filter.user, filter.bauvorhaben]);

  // Only fetch data when dates change, not on every render
  useEffect(() => {
    update_Arbeitszeiten_alle_MA();
    // Not including update_Arbeitszeiten_alle_MA in dependencies
    // since it's a stable function reference
  }, [selectedDateStart, selectedDateEnde]);

  // Create a stable context value object
  const contextValue = useMemo(
    () => ({
      selectedDateStart,
      setSelectedDateStart,
      selectedDateEnde,
      setSelectedDateEnde,
      arbeitszeiten_alle_MA,
      isLoading,
      filter,
      dispatchFilter,
      filteredData,
      availableUsers,
      availableBauvorhaben,
      handle_Upadate_Arbeitszeiten_alle_MA: update_Arbeitszeiten_alle_MA,
      resetFilters,
    }),
    [
      selectedDateStart,
      selectedDateEnde,
      arbeitszeiten_alle_MA,
      isLoading,
      filter,
      filteredData,
      availableUsers,
      availableBauvorhaben,
      update_Arbeitszeiten_alle_MA,
      resetFilters,
    ]
  );

  return (
    <ZeiterfassungDashboardContext.Provider value={contextValue}>
      {children}
    </ZeiterfassungDashboardContext.Provider>
  );
};

export default React.memo(ZeiterfassungDashboardContextProvider);
